import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { Link, useSearchParams } from "react-router-dom";
import { IconCircleCheck, IconAlertTriangle } from "@tabler/icons-react";
import { SCard } from "@/components/Card/SCard";
import { AuthLayout } from "@/pages/auth/AuthLayout";

/**
 * `/sender-verified?ok=1|0` — where the AWS confirmation mail for a single
 * sender address sends the browser after the click.
 *
 * PUBLIC on purpose, and NOT inside `PublicOnlyRoute`: the person clicking is
 * often logged in (they just added the address) and must not be bounced to the
 * dashboard without being told what happened — and just as often it is the
 * mailbox owner on another device with no session at all.
 *
 * No API call: SES has already recorded the click. The page only says which
 * way it went and where to continue; the row flips to VERIFIED on the next
 * status poll / "Reload status" in Veltric.
 */
export const SenderVerifiedPage = () => {
  const t = useTranslations("senderVerified");
  const [params] = useSearchParams();
  const ok = params.get("ok") === "1";

  useEffect(() => {
    window.dispatchEvent(new Event("navigationEnd"));
  }, []);

  const Icon = ok ? IconCircleCheck : IconAlertTriangle;

  return (
    <AuthLayout>
      <SCard header={ok ? t("successTitle") : t("failureTitle")} outline="outline-quaternary" shadow="shadow-xl">
        <div
          data-testid={ok ? "sender-verified-ok" : "sender-verified-failed"}
          className="flex flex-col items-center gap-4 py-6"
        >
          <Icon size={40} className={ok ? "font-success" : "font-error"} aria-hidden="true" />
          <p className="body-2-regular text-center">
            {ok ? t("successMessage") : t("failureMessage")}
          </p>
          <Link to="/" className="body-2-bold underline font-primary">
            {t("backToVeltric")}
          </Link>
        </div>
      </SCard>
    </AuthLayout>
  );
};
